import React from 'react';
import I18n from 'i18n-js';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { RCAvatar, RCStarRating } from '@reverbdotcom/cadence/components';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { SHOP_PATH } from '@reverbdotcom/commons/src/url_helpers';
import { adminEditUserPath } from './shared/url_helpers';
import {
  BumpKeyFragment,
  RefreshedFeaturedListingShopInfoDataFragment,
  arbiter_Interaction_InteractionType,
} from '@reverbdotcom/commons/src/gql/graphql';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useBumpInteractionTracking } from '@reverbdotcom/commons/src/trackBumpInteraction';

interface IProps {
  listing: RefreshedFeaturedListingShopInfoDataFragment & BumpKeyFragment;
}

export const RefreshedFeaturedListingShopInfoFragment = gql(`
  fragment RefreshedFeaturedListingShopInfoData on Listing {
    _id
    id
    seller {
      _id
      id
      avatar {
        _id
        source
      }
      feedbackSummary {
        rollingRatingPercentage
        receivedCount
      }
    }
    shop {
      _id
      name
      slug
      address {
        _id
        displayLocation
      }
    }
  }
`);

export function RefreshedFeaturedListingShopInfo(props: IProps) {
  const { listing } = props;
  const { shop, seller } = listing;

  const currentUser = useUser();
  const bumpInteractionTracking = useBumpInteractionTracking({
    interactionType: arbiter_Interaction_InteractionType.CLICK,
    bumpKey: listing?.bumpKey,
    listingId: listing?.id,
  });

  if (!shop) return null;

  const trackShopNameBumpInteraction = () => {
    bumpInteractionTracking('featuredListing:shopName');
  };

  return (
    <div className="refreshed-featured-listing-shop-info" id={props.listing.id}>
      <div className="d-flex">
        <div className="refreshed-featured-listing-shop-info__avatar">
          <RCAvatar
            userName={shop.name}
            thumbnailURL={seller.avatar?.source}
            thumbnailFallback="shop"
          />
        </div>
        <div>
          <div className="d-flex">
            <CoreLink
              to={SHOP_PATH.expand({ slug: shop.slug })}
              className="weight-bold lh-130 hyphenate color-primary mr-2"
              onClick={trackShopNameBumpInteraction}
            >
              {shop.name}
            </CoreLink>
            <RCStarRating
              rating={seller.feedbackSummary.rollingRatingPercentage / 100}
              count={seller.feedbackSummary.receivedCount}
            />
          </div>
          <div data-location className="size-80 color-secondary lh-130 mt-1">
            {shop.address.displayLocation}
            {currentUser.isAdmin &&
              <CoreLink
                to={adminEditUserPath.expand({ id: seller.id })}
                className="ml-1"
                data-user-edit
              >
                {I18n.t('discovery.item2Shop.viewUser')}
              </CoreLink>
            }
          </div>
        </div>
      </div>
    </div>
  );
}
